import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/features/people/components';
import { TemplatePicker } from '@/features/export/template-picker';
import { isInactiveProject, withArchivedHidden } from '@/lib/archived-projects';
import { ShowArchivedToggle } from '@/components/show-archived-toggle';
import { ProjectFilterLabel } from '@/components/project-filter-label';
import {
  useBuiltReport,
  useDeleteTimeEntry,
  useRefreshTimePeriods,
  useTimePeriodPresets,
  useTimeReport,
  useUpdateTimeEntry,
} from '@/hooks/use-time-entries';
import { usePermission } from '@/lib/permissions';
import { startOfDay } from 'date-fns';
import { FileText, Download, Loader2, ChevronDown, Pencil, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useEmployeeNameFormatter, type NameParts } from '@/lib/employee-name';
import type { TimeEntryItem, TimeReportData, TimeReportGroup } from '@hygge/shared';
import {
  billableSeconds,
  buildReportRows,
  buildReportTableHtml,
  escapeHtml,
  formatDuration,
  formatHours,
  groupBillableSeconds,
  isAdjusted,
  reportBillableSeconds,
  reportSheetColumns,
} from '../lib/report-print';
import { downloadSpreadsheet } from '@/lib/spreadsheet';
import {
  countEntries,
  findReportEntry,
  pageCount,
  pageRows,
  type ReportRow,
} from '../lib/report-rows';
import {
  describeEntryList,
  entryListFiltersLine,
  formatWindow,
  generatedOnText,
  levelLabel,
  onePersonName,
  personFileSuffix,
  type ReportGroupLevel,
} from '../lib/report-builder-state';
import { REPORT_PRESETS, activePresetOf } from '../lib/report-presets';
import { reportPeople } from '../lib/report-people';
import { useReportProjects } from '../lib/time-tracking-projects';
import {
  buildReportsTabConfig,
  reportsWindow,
  summaryWaitsForPeriods,
  type ReportsPeriodPreset,
} from '../lib/reports-tab-config';
import { entryDayKey, entryStartWall, formatEntryDate } from '../lib/wall-clock';
import { viewerTimeZone } from '@/lib/timezone';
import { exportBuiltReport } from '../lib/report-export';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { GroupByPicker } from './group-by-picker';
import { ResultTable } from './report-result-table';
import { EditEntryDialog } from './edit-entry-dialog';
import { DeleteEntryConfirm } from './delete-entry-confirm';
import { reportRowControls } from '../lib/entry-lock';
import { entryWriteFilter, isDeletePendingFor } from '../lib/entry-saves';
import {
  ENTRY_NOT_SAVABLE,
  hoursBoxEvent,
  hoursBoxSeed,
  inlineHoursCommit,
  refusalShowsInBox,
  type HoursBox,
  type HoursBoxCommitVia,
  type HoursBoxEvent,
  type HoursField,
  type InlineCommit,
} from '../lib/hours-input';

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * formatDuration / formatHours live in ../lib/report-print so the screen and the
 * PDF can never format the same number two different ways.
 *
 * Period labels carry the year; entry dates deliberately do not (see the print
 * module) — do not unify them.
 */
function formatDate(iso: string): string {
  // The window's edges are UTC-digit day edges (see `reportsWindow`), so the
  // label reads them in UTC: locally, "Sep 1" west of UTC printed "Aug 31".
  // [HW-916]
  return formatEntryDate(iso, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Page size ────────────────────────────────────────────────────────────

type PageSizeOption = '25' | '50' | '100' | 'all';

const DEFAULT_PAGE_SIZE: PageSizeOption = '50';

/** "All" is Infinity, which report-rows reads as "no paging". */
function pageSizeOf(option: PageSizeOption): number {
  return option === 'all' ? Number.POSITIVE_INFINITY : Number(option);
}

// ─── Grouping ─────────────────────────────────────────────────────────────

/**
 * The two dimensions this screen groups by today, Person first because the
 * screen opens on it. The picker itself takes any of the Builder's levels, so
 * adding a third here is one entry in this list — the ticket asks that the
 * control extend without a redesign. [HW-877]
 */
const REPORT_GROUP_OPTIONS = (['employee', 'project'] as const).map((level) => ({
  level,
  label: levelLabel(level),
}));

// ─── Hour cells ───────────────────────────────────────────────────────────

/** What a cell's save sends — its own field, and for Logged a moved start. */
type HoursPayload = Extract<InlineCommit, { kind: 'save' }>['payload'];

/**
 * One of a Show entries row's two hour cells — Logged or Billable.
 *
 * Read-only unless the row allows it (`reportRowControls`: the server's
 * `canEdit`, and for Billable also "Edit any time log"). Editable, the cell
 * is drawn boxed like a field even at rest, and a click opens a box holding the cell's figure in the Duration box's spelling
 * (`2h 30m` — `hoursBoxSeed` says why not the printed `2.50`); it reads
 * exactly like the Log Time Duration box (`parseDuration`, Billable allowing
 * zero). Enter or leaving the box saves, Escape cancels, an emptied or
 * unchanged box sends nothing — `inlineHoursCommit`. Billable sends
 * `billableDuration` alone; Logged sends `duration`, with the start moved
 * back when the entry would otherwise run past its midnight, as the Edit
 * dialog would. A refusal — the grammar's or the server's — keeps the box
 * open on what was typed, with the reason under it (`hoursBoxEvent`); the
 * update hook suppresses the global toast. A text the server refused is not
 * sent again by leaving the box, only by Enter. One box is open in the table
 * at a time: opening a cell closes any other box not saving, a refused one
 * included, and a save refused after the reader has opened another cell,
 * or after the row left the screen (a page turn, the summary, a filter),
 * closes its box and says why in a toast. The save goes through the row
 * (`onSave`), which holds both cells, the pencil and the trash shut until
 * the report has refetched, so nothing opens on the row as it was before the
 * save. A save moves the group and grand totals, the banner and the exports
 * with it. Billable keeps its adjusted highlight. [HW-974]
 *
 * Until HW-973 the Billable cell held a typed override in a React Map; it is
 * the stored column now, and this edits it through the API.
 */
function HoursCell({
  entry,
  field,
  editable,
  rowSaving,
  active,
  onOpen,
  onSave,
}: {
  entry: TimeEntryItem;
  field: HoursField;
  editable: boolean;
  /** A save of this row — from either cell — is out or still refetching. */
  rowSaving: boolean;
  /** This is the cell the reader opened last in the table. */
  active: boolean;
  onOpen: () => void;
  onSave: (payload: HoursPayload) => Promise<unknown>;
}) {
  const storedSeconds = field === 'logged' ? entry.duration : billableSeconds(entry);
  const seeded = hoursBoxSeed(storedSeconds);
  const [box, setBox] = useState<HoursBox | null>(null);
  // The box as last dispatched, read by the handlers: the blur that can
  // follow Enter or Escape, or the input's own unmount, must see the box
  // those keys left — saving, or closed — not the one this render captured.
  const boxRef = useRef<HoursBox | null>(null);
  const dispatch = (event: HoursBoxEvent) => {
    boxRef.current = hoursBoxEvent(boxRef.current, event);
    setBox(boxRef.current);
  };
  const label = field === 'logged' ? 'Logged Hours' : 'Billable Hours';
  const errorId = `${entry.id}-${field}-error`;
  // Read by the save's answer, which can land after another cell opened or
  // after the row left the screen (`refusalShowsInBox`).
  const activeRef = useRef(active);
  activeRef.current = active;
  const mountedRef = useRef(true);
  useEffect(() => {
    // Set on the way in as well: StrictMode mounts, unmounts and mounts
    // again, and a ref only cleared by the cleanup would stay false.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Another cell opened: this box goes, unless its save is out (the reducer
  // holds a saving box) — that one closes when the save lands, or below when
  // it is refused.
  useEffect(() => {
    if (!active) dispatch({ type: 'cancel' });
  }, [active]);

  const commit = (via: HoursBoxCommitVia) => {
    const current = boxRef.current;
    if (current === null || current.saving) return;
    const decision = inlineHoursCommit({
      field,
      raw: current.text,
      seeded,
      storedSeconds,
      stored: entry,
    });
    dispatch({ type: 'commit', via, decision });
    // Sent only when the box went to saving: a blur on a text the server
    // already refused leaves it as it was.
    if (decision.kind !== 'save' || !boxRef.current?.saving) return;
    onSave(decision.payload).then(
      () => dispatch({ type: 'saved' }),
      (error: unknown) => {
        const message = extractErrorMessage(error, ENTRY_NOT_SAVABLE);
        dispatch({ type: 'failed', message });
        if (refusalShowsInBox({ mounted: mountedRef.current, active: activeRef.current })) return;
        // The reader is in another cell by now — a second open box would
        // stand beside theirs — or the row is gone from the screen; either
        // way this box closes and the reason is a toast.
        dispatch({ type: 'cancel' });
        toast.error(`${label} not saved: ${message}`);
      }
    );
  };

  const value = (
    <span
      className={cn(
        field === 'billable' && isAdjusted(entry) && 'text-hygge',
        rowSaving && 'opacity-50'
      )}
    >
      {formatHours(storedSeconds)}
    </span>
  );
  const cellClass = cn('text-right px-4 tabular-nums', field === 'billable' && 'font-medium');

  if (!editable) {
    return <td className={cn(cellClass, 'py-2')}>{value}</td>;
  }

  if (box !== null) {
    return (
      <td className={cn(cellClass, 'py-1')}>
        <input
          autoFocus
          type="text"
          aria-label={label}
          aria-invalid={box.error !== null}
          aria-describedby={box.error !== null ? errorId : undefined}
          aria-busy={box.saving}
          // Read-only rather than disabled while saving, so focus stays put
          // and a refused value can be retyped where it stands.
          readOnly={box.saving}
          value={box.text}
          onChange={(e) => dispatch({ type: 'type', text: e.target.value })}
          onBlur={() => commit('blur')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit('enter');
            if (e.key === 'Escape') dispatch({ type: 'cancel' });
          }}
          className={cn(
            // The resting box's geometry, so its digits do not move as it opens.
            '-mr-[9px] w-[84px] h-7 rounded border bg-background px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-1',
            box.error !== null
              ? 'border-destructive focus:ring-destructive'
              : 'border-border focus:ring-hygge',
            box.saving && 'opacity-50 cursor-wait'
          )}
        />
        {/* In the flow, not floated: the table clips what overflows it. */}
        {box.error !== null && (
          <p
            id={errorId}
            role="alert"
            className="mt-1 ml-auto max-w-[160px] text-right text-[11px] leading-tight text-destructive"
          >
            {box.error}
          </p>
        )}
      </td>
    );
  }

  // Boxed at rest, like a field, so an editable figure reads as one before it
  // is clicked (2026-10-09 decision); a read-only cell keeps no box. The
  // negative right margin cancels the box's padding and border, so its digits
  // stand in the column of the read-only cells' digits. [HW-974]
  return (
    <td className={cn(cellClass, 'py-1')}>
      <button
        type="button"
        title={`Edit ${label}`}
        aria-label={`Edit ${label}`}
        disabled={rowSaving}
        onClick={() => {
          onOpen();
          dispatch({ type: 'open', seed: seeded });
        }}
        className="-mr-[9px] inline-flex h-7 items-center rounded border border-input bg-background px-2 tabular-nums cursor-pointer transition-colors hover:border-hygge/60 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-hygge disabled:cursor-wait"
      >
        {value}
      </button>
    </td>
  );
}

// ─── Report Table ─────────────────────────────────────────────────────────

interface ReportTableProps {
  report: TimeReportData;
  /** Rows for the current page only. Totals below never read this. */
  visibleRows: ReportRow[];
  formatName: (employee: NameParts) => string;
  /** The viewer holds "Edit any time log" — the Billable cells' gate. [HW-974] */
  holdsEditAll: boolean;
  onEdit: (entry: TimeEntryItem) => void;
  onDelete: (entry: TimeEntryItem) => void;
}

function ReportTable({
  report,
  visibleRows,
  formatName,
  holdsEditAll,
  onEdit,
  onDelete,
}: ReportTableProps) {
  // Whole-report total — deliberately independent of the page on screen, so the
  // footer always describes the same report the PDF exports. Shared with the PDF
  // rather than recomputed, so the two cannot drift.
  const billableTotal = useMemo(() => reportBillableSeconds(report), [report]);
  // Resolved once for the page of rows rather than once per legacy row. [HW-916]
  const viewerZone = viewerTimeZone();
  // The hour cell opened last, `entryId:field`: one box open at a time, so a
  // refused box cannot stand beside the one the reader moved on to. [HW-974]
  const [activeCell, setActiveCell] = useState<string | null>(null);

  if (report.groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <FileText className="h-10 w-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">No time entries found for this period</p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Date</th>
            {report.groupBy === 'employee' && (
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Project</th>
            )}
            {report.groupBy === 'project' && (
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Employee</th>
            )}
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Task</th>
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Description</th>
            <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">
              Logged Hours
            </th>
            <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">
              Billable Hours
            </th>
            {/* Pencil and trash, per row, from the server's flags. [HW-974] */}
            <th className="w-[72px] px-2 py-2.5">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) =>
            row.kind === 'group' ? (
              <GroupBannerRow
                key={`group:${row.group.projectId ?? row.group.employeeId ?? row.group.projectName ?? row.group.employeeName ?? ''}`}
                group={row.group}
                groupBy={report.groupBy}
                continued={row.continued}
              />
            ) : (
              <EntryRow
                key={`entry:${row.entry.id}`}
                entry={row.entry}
                groupBy={report.groupBy}
                formatName={formatName}
                viewerZone={viewerZone}
                holdsEditAll={holdsEditAll}
                activeCell={activeCell}
                onOpenCell={setActiveCell}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            )
          )}
        </tbody>
        <tfoot>
          <tr className="bg-muted/30 border-t border-border font-medium">
            <td className="px-4 py-2.5" colSpan={4}>
              Total
            </td>
            <td className="text-right px-4 py-2.5">{formatHours(report.totalSeconds)}</td>
            <td className="text-right px-4 py-2.5">{formatHours(billableTotal)}</td>
            <td className="px-2 py-2.5" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * The band that introduces a group.
 *
 * Its totals are computed from the group's FULL entry list, never from whatever
 * slice of it happens to be on screen — otherwise every banner would quietly
 * report a per-page subtotal and the "(billable: …)" annotation would fire on
 * groups that, in full, bill exactly what they log.
 */
function GroupBannerRow({
  group,
  groupBy,
  continued,
}: {
  group: TimeReportGroup;
  groupBy: TimeReportData['groupBy'];
  continued: boolean;
}) {
  const label = groupBy === 'project' ? group.projectName : group.employeeName;

  const billableGroupTotal = useMemo(() => groupBillableSeconds(group), [group]);

  return (
    <tr className="bg-muted/20 border-t border-border">
      <td className="px-4 py-2 font-medium text-foreground" colSpan={7}>
        <span className="flex items-center gap-2">
          {group.projectColor && (
            <span
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ backgroundColor: group.projectColor }}
            />
          )}
          {label}
          {continued && <span className="text-muted-foreground font-normal">(continued)</span>}
          <span className="text-muted-foreground font-normal">
            — {formatDuration(group.totalSeconds)}
            {billableGroupTotal !== group.totalSeconds && (
              <span className="text-hygge ml-1">
                (billable: {formatDuration(billableGroupTotal)})
              </span>
            )}
          </span>
        </span>
      </td>
    </tr>
  );
}

function EntryRow({
  entry,
  groupBy,
  formatName,
  viewerZone,
  holdsEditAll,
  activeCell,
  onOpenCell,
  onEdit,
  onDelete,
}: {
  entry: TimeEntryItem;
  groupBy: TimeReportData['groupBy'];
  formatName: (employee: NameParts) => string;
  /** The reader's zone, resolved once by the table. [HW-916] */
  viewerZone: string;
  holdsEditAll: boolean;
  /** The table's open hour cell, `entryId:field`. [HW-974] */
  activeCell: string | null;
  onOpenCell: (cell: string) => void;
  onEdit: (entry: TimeEntryItem) => void;
  onDelete: (entry: TimeEntryItem) => void;
}) {
  // Every control from a server flag; the row's title says why the pencil and
  // the editable cells are missing, on hover, as the timesheet does. A row with
  // a live pencil carries no reason, so none sits over it. [HW-974]
  const controls = reportRowControls(entry, holdsEditAll);
  // One save per row at a time, from either cell, held until the report has
  // refetched: until then the pencil, the trash and both cells are shut, so
  // the Edit dialog or a cell cannot open on the row from before the save —
  // the dialog's Save would send the old length back. The Edit dialog's own
  // save, held the same way, shuts the row too — after the dialog closes on
  // Cancel mid-save as well, so it is read from the mutation cache rather
  // than from this row's hook. A delete of the entry shuts the row the same
  // way, until the report has refetched, so its trash cannot send a second
  // Delete for an entry already gone. [HW-974]
  const updateMutation = useUpdateTimeEntry({ untilRefetched: true });
  const rowSaving = useIsMutating(entryWriteFilter(entry.id)) > 0;
  const save = (payload: HoursPayload) => updateMutation.mutateAsync({ id: entry.id, ...payload });
  return (
    <tr
      className="border-t border-border/50 hover:bg-muted/10 transition-colors"
      title={controls.lockTitle ?? undefined}
    >
      <td className="px-4 py-2 text-muted-foreground">
        {/* The author's day; a legacy row the backfill has not converted yet
            reads on the viewer's clock, as it did before HW-916. */}
        {formatEntryDate(entryStartWall(entry, viewerZone), { month: 'short', day: 'numeric' })}
      </td>
      {groupBy === 'employee' && (
        <td className="px-4 py-2">
          <span className="flex items-center gap-1.5">
            {entry.project.color && (
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.project.color }}
              />
            )}
            {entry.project.name}
          </span>
        </td>
      )}
      {groupBy === 'project' && (
        <td className="px-4 py-2">
          {formatName({ firstName: entry.employee.firstName, lastName: entry.employee.lastName })}
        </td>
      )}
      <td className="px-4 py-2 text-muted-foreground font-mono text-xs">
        {entry.task?.key ?? '—'}
      </td>
      <td className="px-4 py-2 text-muted-foreground truncate max-w-[200px]">
        {entry.description || '—'}
      </td>
      <HoursCell
        entry={entry}
        field="logged"
        editable={controls.loggedCell}
        rowSaving={rowSaving}
        active={activeCell === `${entry.id}:logged`}
        onOpen={() => onOpenCell(`${entry.id}:logged`)}
        onSave={save}
      />
      <HoursCell
        entry={entry}
        field="billable"
        editable={controls.billableCell}
        rowSaving={rowSaving}
        active={activeCell === `${entry.id}:billable`}
        onOpen={() => onOpenCell(`${entry.id}:billable`)}
        onSave={save}
      />
      <td className="px-2 py-1">
        <div className="flex items-center justify-end gap-1">
          {controls.pencil && (
            <button
              type="button"
              title="Edit entry"
              aria-label="Edit entry"
              disabled={rowSaving}
              onClick={() => onEdit(entry)}
              className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {controls.trash && (
            <button
              type="button"
              title="Delete entry"
              aria-label="Delete entry"
              disabled={rowSaving}
              onClick={() => onDelete(entry)}
              className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────

export function ReportsTab() {
  // Outer → inner, in the order picked: the first is the top-level row. Opens
  // on the first preset, By Person — the simplest of the three accounting asked
  // for; the screen used to open on projects.
  const [levels, setLevels] = useState<ReportGroupLevel[]>(['employee']);
  // Off by default: the summarized shape IS the report now, and the entry
  // list — a line per entry with its Logged and Billable Hours — is the mode you switch on
  // when a line per entry is what you need. Offered for a single grouping
  // only: two levels plus every entry is the wall of text this ticket set out
  // to remove. [HW-877]
  const [showEntries, setShowEntries] = useState(false);
  const canShowEntries = levels.length === 1;
  const entriesMode = showEntries && canShowEntries;
  // None while the entry list shows: a preset is a summary by definition.
  const activePreset = activePresetOf(levels, entriesMode);
  // Adding a second level leaves entries mode for good rather than parking the
  // switch: parked, it showed "off" and then turned itself back on the moment
  // the second level was removed — a control that flips without a click.
  const handleLevelsChange = useCallback((next: ReportGroupLevel[]) => {
    setLevels(next);
    if (next.length > 1) setShowEntries(false);
  }, []);
  // The entry list's own endpoint groups one level deep and knows these two
  // by name; this is that level, for the code paths that serve it.
  const groupBy: 'project' | 'employee' = levels[0] === 'project' ? 'project' : 'employee';
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  // One person, or everyone — the question accounting brought: one person's
  // entries with their descriptions, without deleting everybody else's rows
  // from the export by hand. [HW-955]
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('all');
  const [period, setPeriod] = useState<ReportsPeriodPreset>('this-month');
  // Seeded at midnight, not "now": the two fields bound each other via
  // minDate/maxDate, so a seed carrying a time-of-day puts today itself out of
  // range in the other field — typing or picking today was refused.
  const [customFrom, setCustomFrom] = useState<Date | null>(() => startOfDay(new Date()));
  const [customTo, setCustomTo] = useState<Date | null>(() => startOfDay(new Date()));
  const [hideLoggedFromExport, setHideLoggedFromExport] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSizeOption, setPageSizeOption] = useState<PageSizeOption>(DEFAULT_PAGE_SIZE);

  const formatName = useEmployeeNameFormatter();

  // Show entries edits in place: the pencil opens the Edit dialog every Time
  // Tracker surface uses, the trash the same confirmation, and the Billable
  // cells open only to "Edit any time log". [HW-974]
  const holdsEditAll = usePermission('time.edit.all');
  // Held until the report has refetched, as the Edit dialog's save is: the
  // confirmation stays on "Deleting..." and the row stays shut. [HW-974]
  const deleteMutation = useDeleteTimeEntry({ untilRefetched: true });
  // The id, not the row object: the dialog opens on the entry as the report
  // holds it now (`findReportEntry`), never on a copy from before a save.
  // [HW-974]
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<TimeEntryItem | null>(null);

  // The projects this reader's report can contain, from the Time Tracker
  // rather than `/projects`, which narrows a role holding only
  // `projects.view.assigned` to its memberships while a holder of
  // `time.reports.all` or `time.edit.all` reads every project here. [HW-972]
  const { data: projectsData } = useReportProjects();
  const projects = projectsData?.data ?? [];
  // What the Project dropdown lists — active projects, the rest behind the
  // toggle, the selection always. The full `projects` stays the source for
  // names elsewhere (the export's filter labels). [HW-951]
  const filterProjects = useMemo(
    () =>
      withArchivedHidden(projects, (p) => isInactiveProject(p.status), (p) => p.id, {
        showArchived: showArchivedProjects,
        keep: [selectedProjectId],
      }),
    [projects, showArchivedProjects, selectedProjectId]
  );

  // The company calendar's days for the period — the same answer the
  // summarized half is sent, so the two cannot name different periods
  // [HW-929] — as UTC-digit day edges, the days the server files entries under. Until the backfill
  // converts legacy rows, a legacy night row is fetched by its real instant
  // while its Date cell names the reader's day, so near a period's edge it can
  // sit in the neighbouring period's list under a date outside it; the runbook
  // runs the backfill right after this frontend deploys, in the same window,
  // which closes the gap. [HW-916]
  const { data: periods, isLoading: periodsLoading, error: periodsError } = useTimePeriodPresets();
  const dateRange = useMemo(
    () => reportsWindow({ period, customFrom, customTo }, periods),
    [period, customFrom, customTo, periods]
  );

  const reportParams = useMemo(() => {
    if (!dateRange || !entriesMode) return null;
    return {
      groupBy,
      from: dateRange.from,
      to: dateRange.to,
      ...(selectedProjectId !== 'all' ? { projectId: selectedProjectId } : {}),
      ...(selectedEmployeeId !== 'all' ? { employeeId: selectedEmployeeId } : {}),
    };
  }, [entriesMode, groupBy, dateRange, selectedProjectId, selectedEmployeeId]);

  const { data: report, isLoading } = useTimeReport(reportParams);
  const editingEntry = findReportEntry(report, editingId);
  // An entry gone from the report — deleted or moved out of it elsewhere —
  // closes its dialog for good, rather than leaving an id that would reopen it
  // should the entry come back. [HW-974]
  useEffect(() => {
    if (editingId !== null && report && !editingEntry) setEditingId(null);
  }, [editingId, report, editingEntry]);

  // The summarized shapes go to the Builder's runner — `buildReportsTabConfig`
  // says why the two screens share one aggregation, and why a preset goes as
  // the company calendar's days. Null while in entries mode, while a custom
  // range is half-filled, or while the calendar is still loading — a null runs
  // nothing. [HW-929]
  const builtConfig = useMemo(
    () =>
      entriesMode || summaryWaitsForPeriods(period, periods, !!periodsError)
        ? null
        : buildReportsTabConfig(
            {
              period,
              customFrom,
              customTo,
              levels,
              projectId: selectedProjectId,
              employeeId: selectedEmployeeId,
            },
            periods
          ),
    [
      entriesMode,
      period,
      customFrom,
      customTo,
      levels,
      selectedProjectId,
      selectedEmployeeId,
      periods,
      periodsError,
    ]
  );
  const { data: built, isLoading: isBuiltLoading, error: builtError } = useBuiltReport(builtConfig);

  // Who the Person dropdown offers: a by-person summary of the same window and
  // project, left unnarrowed by the person — `reportPeople` says why it is not
  // the employee directory. Grouped by person with nobody chosen, it is the
  // very configuration above, so the two share one request. [HW-955]
  const peopleConfig = useMemo(
    () =>
      summaryWaitsForPeriods(period, periods, !!periodsError)
        ? null
        : buildReportsTabConfig(
            {
              period,
              customFrom,
              customTo,
              levels: ['employee'],
              projectId: selectedProjectId,
              employeeId: 'all',
            },
            periods
          ),
    [period, customFrom, customTo, selectedProjectId, periods, periodsError]
  );
  const { data: peopleReport } = useBuiltReport(peopleConfig);
  // Every name a window has shown, so a chosen person keeps theirs in a window
  // that holds none of their hours.
  const knownNamesRef = useRef(new Map<string, string>());
  const people = useMemo(() => {
    for (const row of peopleReport?.rows ?? []) {
      if (row.keys[0] && row.label[0]) knownNamesRef.current.set(row.keys[0], row.label[0]);
    }
    return reportPeople(peopleReport?.rows, selectedEmployeeId, knownNamesRef.current);
  }, [peopleReport, selectedEmployeeId]);
  const employeeNames = useMemo(() => new Map(people.map((p) => [p.id, p.name])), [people]);

  // ─── Pagination (client-side, over the already-fetched report) ───────────

  const totalEntries = useMemo(() => (report ? countEntries(report) : 0), [report]);
  const pageSize = pageSizeOf(pageSizeOption);
  const totalPages = pageCount(totalEntries, pageSize);
  // A background refetch can shrink the report while the reader sits on the last
  // page, so never trust `page` without clamping it to what exists right now.
  const currentPage = Math.min(page, totalPages);
  const visibleRows = useMemo(
    () => (report ? pageRows(report, currentPage, pageSize) : []),
    [report, currentPage, pageSize]
  );

  // Clamping only fixes the render; `page` itself still holds the stale number.
  // Left alone, a report that shrinks to 2 pages and later grows back to 9 would
  // silently throw the reader from page 2 to page 9 with no interaction. Write
  // the clamp back so what is displayed and what is stored stay the same number.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Everything that changes which entries the report contains. A primitive key,
  // not the params object: an object identity is one refactor away from changing
  // every render, which would strand the reader on page 1 forever.
  const reportKey = `${levels.join('>')}|${entriesMode ? 'entries' : 'summary'}|${selectedProjectId}|${selectedEmployeeId}|${dateRange?.from ?? ''}|${dateRange?.to ?? ''}`;
  useEffect(() => {
    setPage(1);
  }, [reportKey]);

  // Anchors the scroll reset below. The pager sits under a full page of rows, so
  // without this "Next" leaves the reader mid-table on the new page.
  const tableTopRef = useRef<HTMLDivElement>(null);

  const handlePageChange = useCallback((next: number) => {
    setPage(next);
    tableTopRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, []);

  const handlePageSizeChange = useCallback((value: string) => {
    setPageSizeOption(value as PageSizeOption);
    setPage(1);
  }, []);

  /**
   * One row of the export menu, styled like the quick-export rows in People.
   *
   * Every row carries its own `disabled` rather than leaning on the trigger's:
   * Radix leaves an open popover open when its trigger goes disabled, so a
   * refetch that empties the report while the menu is up would otherwise leave
   * rows that look live and do nothing.
   *
   * `close()` sits in a `finally` because the spreadsheet path is synchronous —
   * a throw out of it would otherwise skip the close and leave the menu standing
   * with no download and nothing said.
   */
  const exportItem = (label: string, run: () => void, close: () => void, disabled = false) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        try {
          run();
        } catch {
          toast.error('Export failed. Please try again.');
        } finally {
          close();
        }
      }}
      className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
    >
      <Download className="h-4 w-4" /> {label}
    </button>
  );

  /** Nothing to export: no report loaded, or one that came back empty. */
  const noReport = entriesMode
    ? !report || report.groups.length === 0
    : !built || built.rows.length === 0;

  /**
   * Everything a summarized export needs, from the RESULT the server returned
   * rather than from the controls — the same rule the Builder follows, through
   * the same exporter, so the two screens produce the same files. [HW-877]
   */
  const builtExportInput = () =>
    built && {
      config: built.config,
      window: built.window,
      totalSeconds: built.totalSeconds,
      rows: built.rows,
      generatedOn: new Date(),
      // This screen filters by project and person, so those are the lists it
      // can name ids from; the document counts anything it cannot name.
      filterLabels: {
        projects: new Map(projects.map((p) => [p.id, p.name])),
        employees: employeeNames,
        departments: new Map(),
        positions: new Map(),
        employmentTypes: new Map(),
        projectManagers: new Map(),
      },
    };
  const exportBuilt = (format: 'xlsx' | 'csv' | 'pdf') => {
    const input = builtExportInput();
    if (input) void exportBuiltReport(format, input);
  };

  // A period, project or person change asks the company calendar again, so a page
  // left idle across the company's midnight moves on when the reader acts. [HW-929]
  const refreshPeriods = useRefreshTimePeriods();
  const handlePeriodChange = useCallback(
    (value: string) => {
      setPeriod(value as ReportsPeriodPreset);
      void refreshPeriods();
    },
    [refreshPeriods]
  );

  // What shaped the entry list, for all three of its exports. [HW-953]
  const entryListScope = () => ({
    projectId: selectedProjectId === 'all' ? null : selectedProjectId,
    projectNames: new Map(projects.map((p) => [p.id, p.name])),
    employeeId: selectedEmployeeId === 'all' ? null : selectedEmployeeId,
    employeeNames,
  });
  const entryListPerson = () =>
    onePersonName(selectedEmployeeId === 'all' ? [] : [selectedEmployeeId], employeeNames);
  const entryListAbout = (range: { from: string; to: string }) =>
    describeEntryList({
      ...entryListScope(),
      windowLabel: `${formatDate(range.from)} — ${formatDate(range.to)}`,
      generatedOn: new Date(),
    });

  /**
   * The same report as a spreadsheet, for the accounting side of HW-852: they add
   * these hours up themselves, which a PDF does not allow. Rows and totals come
   * from `buildReportRows`, so this file cannot drift from the PDF — and the
   * Logged Hours column is dropped here exactly when it is dropped there.
   */
  const handleSaveSpreadsheet = (format: 'xlsx' | 'csv') => {
    if (!report || report.groups.length === 0 || !dateRange) return;

    const rows = buildReportRows(report, { hideLogged: hideLoggedFromExport, formatName });
    const columns = reportSheetColumns(groupBy, hideLoggedFromExport);

    // dateRange's edges are UTC-digit days, so their day is the UTC one; read
    // locally, "Sep 1 — Sep 30" on screen would download as
    // 2026-08-31_2026-09-30 west of UTC. [HW-916]
    const period = `${entryDayKey(dateRange.from)}_${entryDayKey(dateRange.to)}`;

    downloadSpreadsheet({
      rows,
      columns,
      filename: `time-report-${groupBy}-${period}${personFileSuffix(entryListPerson())}`,
      format,
      sheetName: 'Time report',
      // The window and the project the list was narrowed to, as the Builder's
      // files state theirs: the dates in each row do not say where the window
      // began or ended, nor that other projects were left out. [HW-953]
      preamble: entryListAbout(dateRange).map((note) => [note.label, note.text]),
    });
  };

  // Deliberately not `async`: there is nothing to await, and the keyword would
  // turn every throw in here into a rejected promise the menu row's synchronous
  // catch cannot see — so the row could report a failed spreadsheet but never a
  // failed PDF.
  const handleSavePdf = () => {
    // The table is built from `report`, not from the DOM, so the export carries
    // every entry no matter which page is on screen.
    //
    // The empty-report check is not just the trigger's `disabled` repeated: Radix
    // leaves an open popover open when its trigger goes disabled, so a refetch
    // that empties the report while the menu is up would otherwise print a
    // window of nothing.
    if (!report || report.groups.length === 0 || !dateRange) return;
    setIsPdfLoading(true);

    // Declared out here so a failure while the document is being written can
    // close the tab it already opened, instead of leaving a blank one behind
    // next to the error toast.
    let printWindow: Window | null = null;

    try {
      printWindow = window.open('', '_blank');
      if (!printWindow) {
        setIsPdfLoading(false);
        toast.error(
          'Your browser blocked the print window. Allow pop-ups for this site to export a PDF.'
        );
        return;
      }
      // A non-null binding: the timer callback below closes over it, and narrowing
      // does not survive a function boundary.
      const openedWindow = printWindow;

      const title = groupBy === 'project' ? `Time Report by Project` : `Time Report by Employee`;
      // The browser names the saved PDF after <title>, so it carries the person
      // the list was narrowed to, as the spreadsheet's file name does. [HW-955]
      const person = entryListPerson();
      const documentTitle = person ? `${title} — ${person}` : title;

      const dateRangeLabel = `${formatDate(dateRange.from)} — ${formatDate(dateRange.to)}`;
      const filtersLine = entryListFiltersLine(entryListScope());

      const totalHours = report?.totalSeconds ? formatHours(report.totalSeconds) : '0.00';

      // Same helper the on-screen footer and the printed tfoot use, so the
      // summary card at the top of the PDF can never disagree with the total at
      // the bottom of it.
      const billableTotalSeconds = reportBillableSeconds(report);
      const billableTotalHours = formatHours(billableTotalSeconds);

      openedWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${escapeHtml(documentTitle)}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 0; color: #1a1a1a; font-size: 13px; }

            .page { padding: 48px; }

            /* Header */
            .report-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #21CE99; }
            .report-header .brand { display: flex; align-items: center; gap: 14px; }
            .report-header .brand svg { height: 36px; width: auto; }
            .report-header .brand-text { font-size: 11px; color: #007553; font-weight: 500; letter-spacing: 0.5px; text-transform: uppercase; }
            .report-header .meta { text-align: right; }
            .report-header .meta h1 { font-size: 18px; font-weight: 600; color: #1a1a1a; margin-bottom: 4px; }
            .report-header .meta p { font-size: 12px; color: #666; line-height: 1.5; }

            /* Summary cards */
            .summary { display: flex; gap: 16px; margin-bottom: 28px; }
            .summary-card { flex: 1; padding: 16px 20px; border-radius: 8px; background: #f8fafa; border: 1px solid #e8edec; }
            .summary-card .label { font-size: 11px; color: #007553; font-weight: 500; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
            .summary-card .value { font-size: 22px; font-weight: 600; color: #1a1a1a; }
            .summary-card .sub { font-size: 11px; color: #888; margin-top: 2px; }

            /* Table */
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            thead th { text-align: left; padding: 10px 14px; background: #f1f5f4; border-bottom: 2px solid #d4dedd; font-weight: 600; color: #556; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
            thead th:last-child, tbody td:last-child, tfoot td:last-child { text-align: right; }
            ${!hideLoggedFromExport ? 'thead th:nth-last-child(2), tbody td:nth-last-child(2), tfoot td:nth-last-child(2) { text-align: right; }' : ''}
            tbody td { padding: 8px 14px; border-bottom: 1px solid #eef1f0; color: #333; }
            tr:hover td { background: #fcfdfd; }
            tfoot td { padding: 10px 14px; background: #f1f5f4; font-weight: 600; border-top: 2px solid #d4dedd; }

            /* Group rows */
            .group-header td { background: #f8fafa; font-weight: 600; padding: 10px 14px; border-top: 1px solid #d4dedd; }
            /* A group banner is a single cell, so it also matches tbody td:last-child above and
               would be right-aligned. Needs the extra specificity to win. */
            tbody tr.group-header td { text-align: left; }

            /* Footer */
            .report-footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e8edec; display: flex; justify-content: space-between; font-size: 10px; color: #999; }

            @media print {
              body { padding: 0; }
              .page { padding: 24px; }
              .summary-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="report-header">
              <div class="brand">
                <svg width="108" height="50" viewBox="0 0 108 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0)">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0 25.0464L25.5425 0L37.557 11.7811L34.6244 14.6568L45.1252 24.9536L34.6244 35.2504L37.557 38.1262L25.4479 50L0 25.0464ZM24.1111 11.1318L27.9114 14.6766L17.2519 25.22L27.9114 35.7634L24.1111 39.399L9.74383 25.22L24.1111 11.1318Z" fill="#21CE99"/>
                    <path d="M36.2048 14.5958L40.0052 11.051L54.3724 25.1392L40.0052 39.3182L36.2048 35.6826L46.8644 25.1392L36.2048 14.5958Z" fill="#1a1a1a"/>
                    <path d="M66.2898 11.0293H61.8208V38.9705H66.2898V11.0293Z" fill="#1a1a1a"/>
                    <path d="M72.9931 11.0293H68.5242V38.9705H72.9931V11.0293Z" fill="#1a1a1a"/>
                    <path d="M61.8208 22.794V27.2058H72.9932V22.794H61.8208Z" fill="#1a1a1a"/>
                    <path d="M73.738 11.0293L77.2767 26.4012C77.4004 26.9359 77.4622 27.4825 77.4622 28.0308V38.9705H81.9311V28.0308C81.9311 27.4825 81.993 26.9359 82.1166 26.4012L85.6553 11.0293H81.1863L79.8441 20.3044C79.8196 20.4727 79.5738 20.4727 79.5492 20.3044L78.207 11.0293H73.738Z" fill="#1a1a1a"/>
                    <path d="M91.6288 11.0293C96.0977 11.0293 97.215 15.0734 97.215 16.9116H93.8528C93.8528 16.544 93.4804 14.3381 91.6184 14.3381C90.5011 14.3381 89.7563 15.0734 89.7563 15.8087V17.6469V19.4852C89.7563 20.2205 89.7563 20.9558 91.6184 20.9558C93.4804 20.9558 93.7284 19.7303 93.8528 19.1175H91.6288V17.6469H97.215V24.2646H93.8528V21.3234C93.8528 24.2646 90.8735 24.2646 89.7563 24.2646C86.0321 24.2646 86.4046 17.6469 86.415 16.9116C86.4254 16.1764 86.415 11.0293 91.6288 11.0293Z" fill="#1a1a1a"/>
                    <path d="M91.6288 25.7354C96.0977 25.7354 97.215 29.7795 97.215 31.6177H93.8528C93.8528 31.2501 93.4804 29.0442 91.6184 29.0442C90.5011 29.0442 89.7563 29.7795 89.7563 30.5148V32.353V34.1912C89.7563 34.9265 89.7563 35.6618 91.6184 35.6618C93.4804 35.6618 93.7284 34.4363 93.8528 33.8236H91.6288V32.353H97.215V38.9706H93.8528V36.0295C93.8528 38.9706 90.8735 38.9706 89.7563 38.9706C86.0321 38.9706 86.4046 32.353 86.415 31.6177C86.4254 30.8824 86.415 25.7354 91.6288 25.7354Z" fill="#1a1a1a"/>
                    <path d="M108 15.4411V11.0293H99.062V34.5587C99.062 37.8511 100.168 38.55 101.142 38.7945C101.732 38.9427 102.349 38.9705 102.958 38.9705H108V34.5587H103.531V27.2058H108V22.794H103.531V15.4411H108Z" fill="#1a1a1a"/>
                  </g>
                  <defs><clipPath id="clip0"><rect width="108" height="50"/></clipPath></defs>
                </svg>
              </div>
              <div class="meta">
                <h1>${title}</h1>
                <p>${dateRangeLabel}</p>
                <p>${escapeHtml(filtersLine)}</p>
                <p>Generated ${generatedOnText(new Date())}</p>
              </div>
            </div>

            <div class="summary">
              ${
                !hideLoggedFromExport
                  ? `<div class="summary-card">
                <div class="label">Logged Hours</div>
                <div class="value">${totalHours}h</div>
                <div class="sub">${formatDuration(report?.totalSeconds ?? 0)}</div>
              </div>`
                  : ''
              }
              <div class="summary-card">
                <div class="label">Billable Hours</div>
                <div class="value">${billableTotalHours}h</div>
                <div class="sub">${formatDuration(billableTotalSeconds)}</div>
              </div>
              <div class="summary-card">
                <div class="label">Grouped By</div>
                <div class="value" style="font-size:18px">${groupBy === 'project' ? 'Project' : 'Employee'}</div>
                <div class="sub">${report?.groups.length ?? 0} ${groupBy === 'project' ? 'projects' : 'people'}</div>
              </div>
              <div class="summary-card">
                <div class="label">Entries</div>
                <div class="value">${report?.groups.reduce((s, g) => s + g.entries.length, 0) ?? 0}</div>
                <div class="sub">time entries</div>
              </div>
            </div>

            ${buildReportTableHtml(report, { hideLogged: hideLoggedFromExport, formatName })}

            <div class="report-footer">
              <span>Hygge Software</span>
              <span>${dateRangeLabel}</span>
            </div>
          </div>
        </body>
        </html>
      `);
      openedWindow.document.close();
      openedWindow.focus();
      // A timer callback runs outside the try below, so it carries its own: a
      // throw from print() or close() (the tab may be gone by now) would
      // otherwise strand the flag set — and escape as an uncaught error, which
      // the user never sees.
      setTimeout(() => {
        try {
          // Closing the tab within the delay is the user's own doing, not a
          // failure to report — and `print` is gone from a discarded context, so
          // reaching for it would throw and toast at them for it.
          if (openedWindow.closed) return;
          openedWindow.print();
          openedWindow.close();
        } catch {
          // print() blocks while its dialog is up, so the tab can be closed
          // *during* the call as well as before it — same user action, still not
          // a failure to report.
          if (!openedWindow.closed) toast.error('Export failed. Please try again.');
        } finally {
          setIsPdfLoading(false);
        }
      }, 250);
    } catch {
      setIsPdfLoading(false);
      printWindow?.close();
      toast.error('Export failed. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* The three shapes accounting reads day to day, one click each. A
          pressed button means the grouping on screen IS that preset — however
          it got there, by the button or by the picker below. Pressing one also
          leaves entries mode: a preset is a summary by definition. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Quick reports</span>
        {REPORT_PRESETS.map((preset) => {
          const active = activePreset === preset.id;
          return (
            <Button
              key={preset.id}
              type="button"
              size="sm"
              // A selected chip, not a primary action: the site's selected
              // picklist item rather than the mint [HW-926]
              variant={active ? 'secondary' : 'outline'}
              aria-pressed={active}
              title={preset.description}
              onClick={() => {
                setLevels([...preset.groupBy]);
                setShowEntries(false);
              }}
            >
              {preset.label}
            </Button>
          );
        })}
      </div>

      {/* Filters. The Group by hint hangs below its field, out of the flow, so
          the row keeps room for it underneath — below the row, and between
          rows when it wraps. */}
      <div className="flex flex-wrap items-end gap-x-4 gap-y-6 pb-5">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">Group by</Label>
          <GroupByPicker
            value={levels}
            options={REPORT_GROUP_OPTIONS}
            onChange={handleLevelsChange}
          />
        </div>

        {/* On screen for every grouping. It used to appear only while grouping by
            project, and a project chosen there outlived the control: switch to By
            Person and the table showed one person and 67h where the report is
            twelve people and 460h, with nothing on the page naming the project.
            A project filter on a per-person report is a fair question — "who
            worked on X" — it just has to be visible. [HW-877] */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs font-medium text-muted-foreground">Project</Label>
            <ShowArchivedToggle
              id="reports-show-archived"
              checked={showArchivedProjects}
              onCheckedChange={setShowArchivedProjects}
            />
          </div>
          <Select
            value={selectedProjectId}
            onValueChange={(value) => {
              setSelectedProjectId(value);
              void refreshPeriods();
            }}
          >
            <SelectTrigger className="h-9 w-[200px] text-sm">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {filterProjects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <ProjectFilterLabel name={p.name} color={p.color} status={p.status} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Beside Project, and for every grouping, for the reason Project is:
            a narrowed report has to show what narrowed it. Lists the people with
            hours in this window and project. [HW-955] */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">Person</Label>
          <Select
            value={selectedEmployeeId}
            onValueChange={(value) => {
              setSelectedEmployeeId(value);
              void refreshPeriods();
            }}
          >
            <SelectTrigger className="h-9 w-[200px] text-sm" aria-label="Person">
              <SelectValue placeholder="Everyone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              {people.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">Period</Label>
          <Select value={period} onValueChange={handlePeriodChange}>
            <SelectTrigger className="h-9 w-[170px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="this-week">This Week</SelectItem>
              <SelectItem value="last-week">Last Week</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {period === 'custom' && (
          <>
            {/* Wide enough for the picker's widest date at either font size the input takes —
                see formatDate in ui/date-picker.tsx. */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">From</Label>
              <DatePicker
                value={customFrom}
                onChange={setCustomFrom}
                placeholder="Start date"
                maxDate={customTo ?? undefined}
                className="w-[220px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">To</Label>
              <DatePicker
                value={customTo}
                onChange={setCustomTo}
                placeholder="End date"
                minDate={customFrom ?? undefined}
                className="w-[220px]"
              />
            </div>
          </>
        )}

        <div className="flex items-center gap-3 ml-auto">
          <label
            className={cn(
              'flex items-center gap-1.5',
              canShowEntries ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
            )}
            title={canShowEntries ? undefined : 'Available with a single grouping'}
          >
            <Switch
              checked={entriesMode}
              disabled={!canShowEntries}
              onCheckedChange={setShowEntries}
              className="scale-75"
            />
            <span className="text-xs text-muted-foreground whitespace-nowrap">Show entries</span>
          </label>

          {/* The switch shapes the Show entries exports only (PDF, Excel, CSV),
              the one mode that prints Logged and Billable side by side. The
              summary and its exports are Logged Hours throughout — there is
              no billable figure there for the logged one to be hidden from. */}
          {entriesMode && (
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Switch
                checked={hideLoggedFromExport}
                onCheckedChange={setHideLoggedFromExport}
                className="scale-75"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Hide logged hours in exports
              </span>
            </label>
          )}

          {/* One button for every format, like the People export — the same picker
              component, opened with no saved templates behind it, so the three
              actions sit where a template list would. */}
          <TemplatePicker
            contentLabel="Export report"
            trigger={
              <Button size="sm" disabled={noReport} className="gap-1.5">
                {isPdfLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Export
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            }
            footer={(close) => (
              <>
                {exportItem(
                  'Excel (.xlsx)',
                  () => (entriesMode ? handleSaveSpreadsheet('xlsx') : exportBuilt('xlsx')),
                  close,
                  noReport
                )}
                {exportItem(
                  'CSV (.csv)',
                  () => (entriesMode ? handleSaveSpreadsheet('csv') : exportBuilt('csv')),
                  close,
                  noReport
                )}
                {exportItem(
                  'PDF',
                  () => (entriesMode ? handleSavePdf() : exportBuilt('pdf')),
                  close,
                  noReport || isPdfLoading
                )}
              </>
            )}
          />
        </div>
      </div>

      {/* Report period info */}
      {(entriesMode ? dateRange : built) && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <FileText className="h-4 w-4" />
          <span>
            {/* The entry list bounds the period by the company calendar's days,
                sent as whole UTC-digit days, and prints what it asked for; a
                summarized report prints the window the SERVER resolved, as the
                Builder does. Both come from one resolver. [HW-929] */}
            Report period:{' '}
            {entriesMode && dateRange
              ? `${formatDate(dateRange.from)} — ${formatDate(dateRange.to)}`
              : built
                ? formatWindow(built.window)
                : ''}
            {entriesMode && report && (
              <span className="ml-2 font-medium text-foreground">
                Total: {formatDuration(report.totalSeconds)}
              </span>
            )}
            {!entriesMode && built && (
              <span className="ml-2 font-medium text-foreground">
                Total: {formatDuration(built.totalSeconds)}
              </span>
            )}
          </span>
        </div>
      )}

      {/* Report preview: the summarized table, or the entry list. */}
      {!entriesMode ? (
        // The summary holds off until the company calendar answers
        // (`summaryWaitsForPeriods`); that wait is loading too. [HW-929]
        isBuiltLoading || (period !== 'custom' && periodsLoading) ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : builtError ? (
          <p className="text-sm text-destructive">
            {extractErrorMessage(builtError, 'The report could not be run.')}
          </p>
        ) : built && built.rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No time entries found for this period</p>
          </div>
        ) : built ? (
          <ResultTable
            rows={built.rows}
            groupBy={built.config.groupBy}
            metrics={built.config.metrics}
            totalSeconds={built.totalSeconds}
          />
        ) : null
      ) : period !== 'custom' && !periods && periodsError ? (
        <p className="text-sm text-destructive">
          {extractErrorMessage(periodsError, 'Could not work out the period.')}
        </p>
      ) : isLoading || (period !== 'custom' && periodsLoading) ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : report ? (
        <div className="space-y-4" ref={tableTopRef}>
          <ReportTable
            report={report}
            visibleRows={visibleRows}
            formatName={formatName}
            holdsEditAll={holdsEditAll}
            onEdit={(entry) => setEditingId(entry.id)}
            onDelete={setDeletingEntry}
          />
          {/* Both pagination controls in one row under the table. The page-size
              select is rendered whenever the report has entries, not only when
              there is more than one page: on "All" there is exactly one page, and
              hiding the control there would leave no way back to 25. */}
          {totalEntries > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="report-page-size"
                  className="text-xs font-medium text-muted-foreground whitespace-nowrap"
                >
                  Rows per page
                </Label>
                <Select value={pageSizeOption} onValueChange={handlePageSizeChange}>
                  <SelectTrigger id="report-page-size" className="h-9 w-[90px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {totalPages > 1 && (
                <div className="flex-1 min-w-[320px]">
                  <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    total={totalEntries}
                    // "All" collapses to a single page, so the limit is the whole report.
                    limit={pageSizeOption === 'all' ? totalEntries : pageSize}
                    onPageChange={handlePageChange}
                    itemLabel="entries"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}

      {editingEntry && (
        <EditEntryDialog
          entry={editingEntry}
          onClose={() => setEditingId(null)}
          // Closes only once the report holds the saved entry. [HW-974]
          untilRefetched
        />
      )}
      <DeleteEntryConfirm
        entry={deletingEntry}
        isPending={isDeletePendingFor(
          deleteMutation.isPending,
          deleteMutation.variables,
          deletingEntry?.id
        )}
        onCancel={() => setDeletingEntry(null)}
        onConfirm={(entry) =>
          // Left open on failure, where the global error toast says why and the
          // Delete button is live again — closing it would report success. The
          // delete invalidates `timeEntryKeys.all`, so the report and its
          // totals refetch, and it closes only once that refetch has landed.
          // The mutation is shared by every row, so it closes only the entry it
          // deleted — by then another row's confirmation may be open.
          // [HW-843] [HW-974]
          deleteMutation.mutate(entry.id, {
            onSuccess: () => setDeletingEntry((open) => (open?.id === entry.id ? null : open)),
          })
        }
      />
    </div>
  );
}
